import React, { useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import './PMScheduleModal.css';

export default function PMScheduleModal({ 
  isOpen, 
  onClose, 
  deviceData = null, 
  onSuccess 
}) {
  const [pmInterval, setPmInterval] = useState('monthly');
  const [nextPmDate, setNextPmDate] = useState('');
  const [deadlineTitle, setDeadlineTitle] = useState('');
  const [deadlineDescription, setDeadlineDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [deleting, setDeleting] = useState(false);

  // Add missing formData state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    category: 'pm_schedule',
    reminder_days: 7,
    pm_interval_months: 1,
    next_pm_date: ''
  });

  // Extract device data for easier access
  const serialNumber = deviceData?.serial_number || '';
  const customerName = deviceData?.customer_name || '';
  const productType = deviceData?.product_type || '';

  useEffect(() => {
    if (deviceData) {
      const title = `PM Schedule - ${deviceData.serial_number}`;
      const description = `Preventive Maintenance for ${deviceData.product_type} (Customer: ${deviceData.customer_name})`;
      
      setDeadlineTitle(title);
      setDeadlineDescription(description);
      
      setFormData(prev => ({
        ...prev,
        title: title,
        description: description
      }));
    }
  }, [deviceData]);

  // Add missing isSameDay function
  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Calendar grid functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long'
    });
  };

  const isToday = (date) => {
    return isSameDay(date, new Date());
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newDate);
    const dateString = newDate.toISOString().split('T')[0];
    setNextPmDate(dateString);
    
    setFormData(prev => ({
      ...prev,
      next_pm_date: dateString,
      due_date: newDate.toISOString().slice(0, 16)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // อัปเดตวันที่ PM ถัดไปเมื่อเปลี่ยนช่วงเวลา
    if (name === 'pm_interval_months') {
      const today = new Date();
      const nextPMDate = new Date(today);
      nextPMDate.setMonth(nextPMDate.getMonth() + parseInt(value));
      
      setFormData(prev => ({
        ...prev,
        next_pm_date: nextPMDate.toISOString().slice(0, 10),
        due_date: nextPMDate.toISOString().slice(0, 16)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // สร้าง PM schedule โดยตรง
      const pmScheduleResponse = await fetch('/api/pm-service/schedules/update-next-pm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          serial_number: serialNumber,
          next_pm_date: nextPmDate,
          pm_interval_months: pmInterval === 'monthly' ? 1 : 
                             pmInterval === 'quarterly' ? 3 :
                             pmInterval === 'semi-annual' ? 6 : 12,
          customer_name: customerName,
          product_type: productType,
          priority: priority,
          reminder_days: 7,
          notes: `สร้างจาก PM Schedule Modal - ${deadlineDescription}`
        }),
      });

      if (!pmScheduleResponse.ok) {
        throw new Error('ไม่สามารถสร้าง PM schedule ได้');
      }

      const pmScheduleResult = await pmScheduleResponse.json();

      // แจ้งผลสำเร็จ
      if (onSuccess) {
        onSuccess(pmScheduleResult);
      }

      onClose();
      alert('สร้างตารางเวลา PM เรียบร้อยแล้ว');

    } catch (err) {
      setError('เกิดข้อผิดพลาด: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCascadeDeleteAllSchedulesForSerial = async () => {
    if (!serialNumber) {
      alert('ไม่พบ Serial Number สำหรับการลบตาราง PM');
      return;
    }

    const confirmMessage = `คุณต้องการลบ PM Schedule ทั้งหมดของ Serial: ${serialNumber} ใช่หรือไม่?\n\nการดำเนินการนี้จะลบข้อมูลการตรวจสภาพที่เกี่ยวข้องด้วย (Cascade).`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    setError('');
    try {
      const result = await apiService.deleteSchedulesBySerial(serialNumber);
      if (result.success) {
        alert(`ลบ PM Schedule สำเร็จ จำนวน ${result.deleted} รายการ`);
        if (onSuccess) onSuccess({ deleted: result.deleted });
        onClose();
      } else {
        throw new Error(result.error || 'ไม่สามารถลบ PM Schedule ได้');
      }
    } catch (err) {
      console.error('Error cascade deleting schedules:', err);
      setError('เกิดข้อผิดพลาดในการลบ: ' + err.message);
      alert('เกิดข้อผิดพลาดในการลบ: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Render calendar grid
  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isSelected = isSameDay(date, selectedDate);
      const isTodayDate = isToday(date);
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isTodayDate ? 'today' : ''}`}
          onClick={() => handleDateSelect(day)}
        >
          {day}
        </div>
      );
    }
    
    return days;
  };

  if (!isOpen) return null;

  return (
    <div className="pm-schedule-modal-overlay">
      <div className="pm-schedule-modal">
        <div className="pm-modal-header">
          <h2>กำหนดการ PM Schedule</h2>
          <button className="pm-close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="pm-error-message">
            {error}
          </div>
        )}

        <div className="pm-modal-body">
          {deviceData && (
            <div className="pm-device-info">
              <h3>ข้อมูลอุปกรณ์</h3>
              <p><strong>Serial Number:</strong> {deviceData.serial_number}</p>
              <p><strong>ประเภทสินค้า:</strong> {deviceData.product_type}</p>
              <p><strong>ลูกค้า:</strong> {deviceData.customer_name}</p>
            </div>
          )}

          <div className="calendar-section">
            <div className="calendar-header">
              <div className="calendar-navigation">
                <button className="nav-btn" onClick={() => navigateMonth(-1)}>
                  ‹
                </button>
                <h3 className="current-month">{formatDate(currentDate)}</h3>
                <button className="nav-btn" onClick={() => navigateMonth(1)}>
                  ›
                </button>
              </div>
              <button className="today-btn" onClick={goToToday}>
                วันนี้
              </button>
            </div>

            <div className="calendar-grid">
              <div className="calendar-weekdays">
                <div className="weekday">อา</div>
                <div className="weekday">จ</div>
                <div className="weekday">อ</div>
                <div className="weekday">พ</div>
                <div className="weekday">พฤ</div>
                <div className="weekday">ศ</div>
                <div className="weekday">ส</div>
              </div>
              <div className="calendar-days">
                {renderCalendarGrid()}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label>ช่วงเวลา PM:</label>
              <select 
                value={pmInterval} 
                onChange={(e) => setPmInterval(e.target.value)}
              >
                <option value="monthly">รายเดือน</option>
                <option value="quarterly">รายไตรมาส</option>
                <option value="semi-annual">ทุก 6 เดือน</option>
                <option value="annual">รายปี</option>
              </select>
            </div>

            <div className="form-group">
              <label>วันที่เลือก:</label>
              <input
                type="date"
                value={nextPmDate}
                onChange={(e) => {
                  setNextPmDate(e.target.value);
                  setSelectedDate(new Date(e.target.value));
                }}
                required
              />
            </div>

            <div className="form-group">
              <label>หัวข้อ Deadline:</label>
              <input
                type="text"
                value={deadlineTitle}
                onChange={(e) => setDeadlineTitle(e.target.value)}
                placeholder="หัวข้อของ deadline"
                required
              />
            </div>

            <div className="form-group">
              <label>รายละเอียด:</label>
              <textarea
                value={deadlineDescription}
                onChange={(e) => setDeadlineDescription(e.target.value)}
                placeholder="รายละเอียดของ deadline"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>ระดับความสำคัญ:</label>
              <select 
                value={priority} 
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="low">ต่ำ</option>
                <option value="medium">ปานกลาง</option>
                <option value="high">สูง</option>
                <option value="urgent">เร่งด่วน</option>
              </select>
            </div>
          </div>
        </div>

        <div className="pm-modal-footer">
          <button 
            className="pm-btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            ยกเลิก
          </button>
          <button 
            className="pm-btn-primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
          <button
            className="pm-btn-danger"
            onClick={handleCascadeDeleteAllSchedulesForSerial}
            disabled={deleting}
            title="ลบ PM Schedule ทั้งหมดของ Serial นี้ (รวม Inspection)"
            style={{ marginLeft: '8px' }}
          >
            {deleting ? 'กำลังลบ...' : 'ลบทั้งหมด (Cascade)'}
          </button>
        </div>
      </div>
    </div>
  );
}